import { festivalWindow } from '@shared/dates'
import { EVERYWHERE_REGION_ID, isNearby, proximityTier, type ProximityTier } from '@shared/regions'
import type { SearchArtist, SearchEvent, SearchIndex, SearchSet } from '@shared/searchIndex'
import { isEventActive } from './formatDate'

// Pure ranking for global search. The text score comes from MiniSearch; this
// layers on what makes a hit relevant to *this* viewer — upcoming over past,
// their region, their and their friends' plans — and applies the cancelled-event
// stake rule (#330) so search can't surface what browse hides.

export interface RankContext {
  now: Date
  homeRegionId: string | undefined
  myEventIds: ReadonlySet<string>
  friendEventIds: ReadonlySet<string>
  hasStake: (eventId: string) => boolean
}

export interface Hit {
  idx: number
  score: number
}

export interface RankedEvent {
  event: SearchEvent
  past: boolean
  away: boolean
}

export type Appearance =
  | { kind: 'set'; set: SearchSet; event: SearchEvent; past: boolean; away: boolean }
  | { kind: 'lineup'; event: SearchEvent; past: boolean; away: boolean }

export interface RankedArtist {
  artist: SearchArtist
  appearances: Appearance[]
}

const REGION_BOOST: Record<ProximityTier, number> = { same: 2, nearby: 1.5, drivable: 1.2, far: 1 }

function tierOf(ev: SearchEvent, ctx: RankContext): ProximityTier {
  const home = ctx.homeRegionId === EVERYWHERE_REGION_ID ? undefined : ctx.homeRegionId
  return proximityTier(home, ev.regionId)
}

export function regionBoost(ev: SearchEvent, ctx: RankContext): number {
  return REGION_BOOST[tierOf(ev, ctx)]
}

/**
 * Whether an event sits outside the viewer's reach, by the same rule browse uses
 * to split "near you" from the rest. Unknown on either side (no home region, or
 * an event we couldn't place) is never away — a wrong "Far" on a local event is
 * worse than a missing one.
 */
export function isAway(ev: SearchEvent, homeRegionId: string | undefined): boolean {
  if (!homeRegionId || homeRegionId === EVERYWHERE_REGION_ID || !ev.regionId) return false
  return !isNearby(homeRegionId, ev.regionId, ev.type)
}

export function socialBoost(ev: SearchEvent, ctx: RankContext): number {
  return ctx.myEventIds.has(ev.id) || ctx.friendEventIds.has(ev.id) ? 1.5 : 1
}

/** A friend's interest is a discovery signal, not a stake — it doesn't reveal a cancelled event. */
export function isSearchVisible(ev: SearchEvent, ctx: RankContext): boolean {
  return !ev.cancelled || ctx.hasStake(ev.id)
}

export function isUpcoming(ev: SearchEvent, now: Date): boolean {
  return isEventActive(festivalWindow(ev), now)
}

const endOf = (ev: SearchEvent) => festivalWindow(ev).endDate ?? ev.startDate

export function rankEvents(hits: Hit[], index: SearchIndex, ctx: RankContext): RankedEvent[] {
  const upcoming: { event: SearchEvent; score: number }[] = []
  const past: SearchEvent[] = []
  for (const { idx, score } of hits) {
    const event = index.events[idx]
    if (!isSearchVisible(event, ctx)) continue
    if (isUpcoming(event, ctx.now)) upcoming.push({ event, score: score * regionBoost(event, ctx) * socialBoost(event, ctx) })
    else past.push(event)
  }
  upcoming.sort((a, b) => b.score - a.score || a.event.startDate.localeCompare(b.event.startDate))
  past.sort((a, b) => endOf(b).localeCompare(endOf(a)))
  return [
    ...upcoming.map(u => ({ event: u.event, past: false, away: isAway(u.event, ctx.homeRegionId) })),
    ...past.map(event => ({ event, past: true, away: isAway(event, ctx.homeRegionId) })),
  ]
}

export function artistAppearances(artist: SearchArtist, index: SearchIndex, ctx: RankContext, limit = 3): Appearance[] {
  const setsByEvent = new Map<number, SearchSet[]>()
  for (const si of artist.setIdx) {
    const set = index.sets[si]
    const list = setsByEvent.get(set.eventIdx) ?? []
    list.push(set)
    setsByEvent.set(set.eventIdx, list)
  }

  const visible = artist.eventIdx.filter(i => isSearchVisible(index.events[i], ctx))
  const upcoming = visible
    .filter(i => isUpcoming(index.events[i], ctx.now))
    .sort((a, b) => {
      const ea = index.events[a], eb = index.events[b]
      return regionBoost(eb, ctx) - regionBoost(ea, ctx) || ea.startDate.localeCompare(eb.startDate)
    })
  const chosen = upcoming.length > 0
    ? upcoming
    : visible.sort((a, b) => endOf(index.events[b]).localeCompare(endOf(index.events[a]))).slice(0, 1)
  const past = upcoming.length === 0

  const out: Appearance[] = []
  for (const ei of chosen) {
    const event = index.events[ei]
    const away = isAway(event, ctx.homeRegionId)
    const sets = setsByEvent.get(ei) ?? []
    if (sets.length === 0) out.push({ kind: 'lineup', event, past, away })
    else {
      sets.sort((a, b) => a.day.localeCompare(b.day) || a.startTime.localeCompare(b.startTime))
      for (const set of sets) out.push({ kind: 'set', set, event, past, away })
    }
    if (out.length >= limit) break
  }
  return out.slice(0, limit)
}

export function rankArtists(hits: Hit[], index: SearchIndex, ctx: RankContext): RankedArtist[] {
  const ranked = hits.flatMap(({ idx, score }) => {
    const artist = index.artists[idx]
    const appearances = artistAppearances(artist, index, ctx)
    if (appearances.length === 0) return []
    const live = appearances.filter(a => !a.past)
    const boost = Math.max(1, ...live.map(a => regionBoost(a.event, ctx) * socialBoost(a.event, ctx)))
    return [{ artist, appearances, past: live.length === 0, score: score * boost }]
  })
  ranked.sort((a, b) => Number(a.past) - Number(b.past) || b.score - a.score)
  return ranked.map(({ artist, appearances }) => ({ artist, appearances }))
}
